import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Calendar, MapPin, ExternalLink, Newspaper, FileText, Download } from 'lucide-react';
import PdfThumbnail from './PdfThumbnail';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (!u ? '' : u.startsWith('http') ? u : `${BACKEND_URL}${u}`);
import { useContent } from '../hooks/useContent';
import { SectionHeading } from './SectionHeading';

/**
 * Generic CMS-driven section for article-style content
 * (HQ Events, Branch Activities, Media Coverage, Media Statements).
 *
 * Hides itself entirely if the CMS list is empty.
 */
export const CMSArticleSection = ({
  anchor,
  slug,
  eyebrow,
  title,
  accent,
  subtitle,
  background = 'bg-white',
  variant = 'default', // 'default' | 'media'
  testId,
}) => {
  const { data, loading } = useContent(`articles/${slug}`, []);
  const items = data || [];

  if (!loading && items.length === 0) return null;

  return (
    <section id={anchor} className={`py-20 md:py-24 ${background}`} data-testid={testId || `${anchor}-section`}>
      <div className="max-w-7xl mx-auto px-4">
        <SectionHeading eyebrow={eyebrow} title={title} accent={accent} subtitle={subtitle} />

        <div className="grid gap-6">
          {items.map((it) => (
            <Card
              key={it.id}
              className="group overflow-hidden border-2 border-gray-100 hover:border-amber-200 hover:shadow-2xl transition-all"
              data-testid={`${anchor}-item`}
            >
              <CardContent className="p-0">
                <div className="flex flex-col md:flex-row">
                  {variant === 'pdf' ? (
                    <div className="md:w-56 md:flex-shrink-0 bg-gradient-to-br from-rose-50 to-white overflow-hidden">
                      {it.pdf_url ? (
                        <PdfThumbnail src={resolveUrl(it.pdf_url)} className="w-full h-56 md:h-full" />
                      ) : (
                        <div className="w-full h-56 md:h-full bg-gradient-to-br from-rose-500 via-pink-600 to-rose-700 flex items-center justify-center">
                          <div className="text-center text-white">
                            <FileText className="w-16 h-16 mx-auto mb-2 drop-shadow-lg" />
                            <div className="text-[11px] uppercase tracking-widest font-bold opacity-90">PDF</div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : it.image && (
                    <div className="md:w-72 md:flex-shrink-0 bg-gray-100 overflow-hidden">
                      <img
                        src={it.image}
                        alt={it.title}
                        className="w-full h-56 md:h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="flex-1 p-6 md:p-8">
                    {variant === 'media' && it.source && (
                      <Badge className="mb-3 bg-gradient-to-r from-[#0D47A1] to-[#1565C0] text-white border-0 text-[10px] font-bold uppercase tracking-widest">
                        <Newspaper className="w-3 h-3 mr-1.5" />
                        {it.source}
                      </Badge>
                    )}
                    <h3 className="text-xl md:text-2xl font-bold text-[#0A1628] mb-2 group-hover:text-orange-600 transition-colors">
                      {it.title}
                    </h3>
                    {it.subtitle && (
                      <p className="text-base text-[#1565C0] font-semibold mb-3">{it.subtitle}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500 mb-4">
                      {it.date && (
                        <span className="inline-flex items-center gap-1.5">
                          <Calendar className="w-4 h-4 text-orange-500" /> {it.date}
                        </span>
                      )}
                      {it.location && (
                        <span className="inline-flex items-center gap-1.5">
                          <MapPin className="w-4 h-4 text-orange-500" /> {it.location}
                        </span>
                      )}
                    </div>
                    {it.description && (
                      <div
                        className="prose prose-sm max-w-none text-gray-600 leading-relaxed"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: it.description }}
                      />
                    )}
                    {it.link && (
                      <a
                        href={it.link}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-1.5 mt-4 text-sm font-bold uppercase tracking-wider text-[#1565C0] hover:text-orange-600 transition-colors"
                      >
                        {it.link_label || 'Read more'}
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {variant === 'pdf' && it.pdf_url && (
                      <div className="mt-4 flex flex-wrap items-center gap-3">
                        <a
                          href={resolveUrl(it.pdf_url)}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white text-xs font-bold uppercase tracking-wider shadow-lg hover:shadow-xl transition-all"
                          data-testid={`newsletter-open-pdf-${it.id || it._id || ''}`}
                        >
                          <FileText className="w-3.5 h-3.5" /> Open PDF
                        </a>
                        <a
                          href={resolveUrl(it.pdf_url)}
                          download
                          className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#1565C0] hover:text-orange-600"
                          data-testid={`newsletter-download-pdf-${it.id || it._id || ''}`}
                        >
                          <Download className="w-3.5 h-3.5" /> Download
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CMSArticleSection;
